import React, { useEffect, useState } from 'react';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { FiscalYearRestDataProvider } from '../../../data/FiscalYearRestDataProvider';
import { FiscalYear, FiscalYearState, FiscalYearStateLabels } from '../../../models/FiscalYear';
import { toast } from 'react-toastify';
import './FiscalYearsTab.scss';
import { useTranslation } from 'react-i18next';

const isProtected = (fiscalYear: FiscalYear): boolean =>
    fiscalYear.state === FiscalYearState.Closed;

const FiscalYearsTab: React.FC = () => {
    const { t } = useTranslation(['common', 'screens', 'validation']);
    const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const provider = React.useMemo(() => new FiscalYearRestDataProvider(), []);

    const columns: DetailedListColumn<FiscalYear>[] = React.useMemo(() => [
        { key: 'name', header: t('screens:fiscalYears.columns.name'), dataField: 'name', type: 'string', required: true },
        { key: 'start', header: t('screens:fiscalYears.columns.startDate'), dataField: 'start', type: 'date' },
        { key: 'end', header: t('screens:fiscalYears.columns.endDate'), dataField: 'end', type: 'date' },
        {
            key: 'state',
            header: t('screens:fiscalYears.columns.state'),
            dataField: 'state',
            type: 'select',
            options: [
                { label: FiscalYearStateLabels[FiscalYearState.Open], value: FiscalYearState.Open },
                { label: FiscalYearStateLabels[FiscalYearState.Closing], value: FiscalYearState.Closing },
                { label: FiscalYearStateLabels[FiscalYearState.Closed], value: FiscalYearState.Closed },
            ],
            render: (fy: FiscalYear) => FiscalYearStateLabels[fy.state]
        }
    ], [t]);

    useEffect(() => {
        setLoading(true);
        provider.getAll()
            .then(setFiscalYears)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [provider]);

    const handleChange = async (newFiscalYears: FiscalYear[], action: 'add' | 'edit' | 'delete', changed: FiscalYear) => {
        try {
            if (action === 'add') {
                await provider.add(changed);
            } else if (action === 'edit' && !isProtected(changed)) {
                await provider.update(changed.id, changed);
            } else if (action === 'delete' && !isProtected(changed)) {
                await provider.delete(changed.id);
            } else if (isProtected(changed)) {
                toast.error(t('validation:fiscalYearClosed'));
            }

            const refreshed = await provider.getAll();
            setFiscalYears(refreshed);
        } catch (e: any) {
            setError(e.message);
        }
    };

    return (
        <div className="fiscal-years-tab">
            <SubsectionPanel title={t('screens:fiscalYears.title')}>
                {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
                <InfoPanel header="About fiscal years">
                    Fiscal years define the accounting periods for your business. Closed fiscal years are protected and cannot be modified or deleted.
                </InfoPanel>
                <DetailedListComponent
                    dataSource={{ data: fiscalYears, setData: setFiscalYears }}
                    columns={columns}
                    rowKey={r => r.id}
                    onChange={handleChange}
                />
                {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
            </SubsectionPanel>
        </div>
    );
};

export default FiscalYearsTab;
